import { ga4StreamName } from "env";
import { getConfigId, getApplicationId, getPlatform, getLanguageCode, getTenant } from "../storage";
import { AnalyticsEvent } from "./analyticsEvents";

// Tenant/session context every event needs to be actionable in reports —
// which config/app/platform/language/tenant it came from, and the raw
// query string it landed on (config_id, application_id, etc. are already
// parsed out of it into their own fields, but anything else on the URL —
// utm_*, user_code, ad-hoc debug params — is only recoverable from this).
//
// `null`, not `undefined`, for anything missing — JSON.stringify drops
// undefined-valued keys entirely, which is why they'd vanish from any
// dataLayer/GTM debugger that serializes the payload (e.g. TagHound);
// `null` survives serialization, so every field always shows up.
function commonParams(): Record<string, unknown> {
  return {
    config_id: getConfigId() ?? null,
    application_id: getApplicationId() ?? null,
    platform: getPlatform() ?? null,
    language_code: getLanguageCode() ?? null,
    // Strapi's `tenant` field for this tenant's config (see
    // components/AppInitializer, which persists it once the config loads).
    tenant: getTenant() ?? null,
    query_params: window.location.search || null,
  };
}

// Every custom event is tagged with `stream_name` — always the literal GA4
// Stream Name (REACT_APP_GA4_STREAM_NAME), same value on every event
// regardless of tenant — plus the common tenant/session context above.
//
// Pushed to the dataLayer in GTM's `{event, ...params}` shape (matches the
// reference implementation) AND sent directly via gtag.js, so hits reach
// GA4 right now with no GTM container required, while staying in the same
// shape a GTM container could pick up later if one gets added.
export function trackEvent(name: AnalyticsEvent, params: Record<string, unknown> = {}): void {
  const payload = { stream_name: ga4StreamName, ...commonParams(), ...params };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...payload });

  if (!window.gtag) {
    console.warn(`[analytics] trackEvent("${name}") called before gtag.js loaded — not sent to GA4`, payload);
    return;
  }
  console.log(`[analytics] trackEvent("${name}")`, payload);
  window.gtag("event", name, payload);
}
